import { Box, useDisclosure } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { isString } from 'lodash';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import clientApi from '~/clientApi';
import { Creator } from '~/clientApi/creator.model';
import LeavePageBoundary from '~/components/LeavePageBoundary';
import { ManagedCreatorInvitationList } from '~/components/ManagedCreatorInvitationList';
import {
  BENCHMARK_ENTITY_QUERY_KEY,
  CONTENTS_PDF_COLLECTION_QUERY_KEY,
  CREATOR_INSIGHT_ENTITY_QUERY_KEY,
} from '~/queries/keys';
import { selectCreator } from '~/selectors/creator';
import { DefaultMediaPackType, InstagramInsightType } from '~/types';

import { MediaPackDetailDefaultScreenComponent } from './MediaPackDetailDefaultScreen';
import { MediaPackDetailFirstTimeModal } from './components';
import { MediaPackDetailEditAboutModal } from './components/MediaPackDetailEditAboutModal';

interface Props {
  mediaPack: DefaultMediaPackType;
  isLoading?: boolean;
  isSharePage?: boolean;
}

export const MediaPackDetailDefaultScreen: React.FC<Props> = ({
  mediaPack,
  isLoading = false,
  isSharePage,
}) => {
  const router = useRouter();
  const { creatorSlug } = router.query;
  const creator = useSelector(selectCreator);

  // Modal disclosures
  const {
    isOpen: isFirstTimeOpen,
    onClose: onFirstTimeClose,
    onOpen: onFirstTimeOpen,
  } = useDisclosure();
  const { isOpen: isEditOpen } = useDisclosure();
  const {
    onOpen: onEditAboutOpen,
    isOpen: isEditAboutOpen,
    onClose: onEditAboutClose,
  } = useDisclosure();

  const { firstTime } = router.query;

  const isFirstTime = !!isString(firstTime);
  const benchmarkId = creator?.benchmark?.id;

  const { data: creatorBenchmarks = {} } = useQuery(
    [BENCHMARK_ENTITY_QUERY_KEY, benchmarkId],
    () =>
      clientApi.benchmarks.read({
        id: benchmarkId,
        _impersonateOnce: creator?.id,
      }),
    { enabled: Boolean(benchmarkId) && Boolean(creator?.id) },
  );

  const { data: igInsights = {} } = useQuery(
    [CREATOR_INSIGHT_ENTITY_QUERY_KEY],
    () =>
      clientApi.mediaPacks.igInsights({
        id: mediaPack?.id,
        _impersonateOnce: creator?.id,
      }),
    { enabled: Boolean(mediaPack?.id) && Boolean(creator?.id) },
  );

  const { data: pdfList } = useQuery([CONTENTS_PDF_COLLECTION_QUERY_KEY], () =>
    (clientApi as any).content.pdfList({ _impersonateOnce: creator?.id }),
  );

  const pdfImages = pdfList?.results;

  useEffect(() => {
    if (isFirstTime) {
      onFirstTimeOpen();
    }
  }, [isFirstTime, onFirstTimeOpen]);

  return (
    <Box>
      <MediaPackDetailDefaultScreenComponent
        isLoading={isLoading}
        creatorBenchmarks={creatorBenchmarks}
        creatorInsights={igInsights as InstagramInsightType}
        mediaPack={mediaPack}
        onOpenAboutYou={onEditAboutOpen}
        pdfImages={pdfImages}
        isSharePage={isSharePage}
        creatorSlug={creatorSlug as string}
      />

      {isFirstTime ? (
        <MediaPackDetailFirstTimeModal
          creator={creator as Creator}
          isOpen={isFirstTimeOpen}
          mediaPack={mediaPack}
          onClose={onFirstTimeClose}
          creatorSlugParam={creatorSlug as string}
        />
      ) : (
        <>
          <MediaPackDetailEditAboutModal
            creator={creator as Creator}
            isOpen={isEditAboutOpen}
            mediaPack={mediaPack}
            onClose={onEditAboutClose}
          />
        </>
      )}

      {!isSharePage && <ManagedCreatorInvitationList />}

      <LeavePageBoundary
        isEnabled={isFirstTimeOpen || isEditOpen || isEditAboutOpen}
      />
    </Box>
  );
};
